import React, {createContext, useContext, useEffect, useState} from 'react';
import {Box, CircularProgress, IconButton, Typography} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import * as MuiIcons from '@mui/icons-material';
import {getPicto} from '../../../../utils/pictoCatalog.js';

/** Tokens de carte (handoff README §Tokens). */
export const CARD_SX = {
    bgcolor: '#fff',
    border: '1px solid #E3E7EC',
    borderRadius: '16px',
    boxShadow: '0 1px 2px rgba(16,46,105,.06), 0 1px 3px rgba(16,46,105,.05)',
    overflow: 'hidden',
};

/** 11 px / 700 / .12em / uppercase / #8A929C */
export const Eyebrow = ({children, sx}) => (
    <Typography component="div" sx={{fontSize: 11, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: '#8A929C', ...sx}}>
        {children}
    </Typography>
);

/** Icône Material par nom (noms stockés en base, contrat INT-01). */
export const Picto = ({name, ...props}) => {
    const Icon = MuiIcons[getPicto(name).mui] || MuiIcons.Help;
    return <Icon {...props}/>;
};

/** Étiquette blanche uppercase en pill posée sur une photo. */
export const PhotoLabel = ({label, color}) => (
    <Box sx={{
        position: 'absolute', top: 10, left: 10, px: 1, py: 0.25, borderRadius: 999,
        bgcolor: color, color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
        maxWidth: 'calc(100% - 20px)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    }}>
        {label}
    </Box>
);

/**
 * Réglages du carrousel des tuiles photo, fournis par la vue (InterventionViewV2) :
 * `autoPlay` (défaut true, comme le PhotoCarousel legacy), `interval` ms, `duration` ms de la glissade.
 */
export const PhotoTileSettingsContext = createContext({autoPlay: true, interval: 4000, duration: 350});

/**
 * Photo 3/4 portrait, cover, radius 12, zoom-in. `photos` = chemins relatifs.
 * Plusieurs photos → carrousel : **toutes les images sont montées** (préchargées dès l'affichage de la
 * tuile, comme le PhotoCarousel legacy) et le changement est une glissade CSS immédiate, pas un
 * nouveau fetch. Flèches, points, compteur, autoplay (pause au survol). `onOpen` reçoit le chemin affiché.
 * Sans photo : cadre en pointillé « Aucune photo ».
 */
export const PhotoTile = ({photos = [], label, color, assetsUrl, onOpen, maxWidth, autoPlay, interval, duration}) => {
    const settings = useContext(PhotoTileSettingsContext);
    const play = autoPlay ?? settings.autoPlay;
    const every = interval ?? settings.interval;
    const slide = duration ?? settings.duration;

    const [index, setIndex] = useState(0);
    const [hover, setHover] = useState(false);
    const [loaded, setLoaded] = useState({});
    const count = photos.length;
    const photosKey = photos.join('|');

    useEffect(() => {
        setIndex(0);
        setLoaded({});
    }, [photosKey]);

    useEffect(() => {
        if (!play || hover || count < 2) return undefined;
        const id = setInterval(() => setIndex((i) => (i + 1) % count), every);
        return () => clearInterval(id);
    }, [play, hover, count, every]);

    const current = count > 0 ? Math.min(index, count - 1) : -1;
    const src = current >= 0 ? photos[current] : null;
    const go = (e, target) => {
        e.stopPropagation();
        setIndex(((target % count) + count) % count);
    };

    return (
        <Box sx={{position: 'relative', width: '100%', maxWidth, aspectRatio: '3 / 4', borderRadius: '12px', overflow: 'hidden',
            bgcolor: '#EEF2EC', border: src ? 'none' : '1px dashed #C9D4E8',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: src ? 'zoom-in' : 'default'}}
             onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
             onClick={src ? () => onOpen?.(src) : undefined}>
            {count > 0 ? (
                <Box sx={{position: 'absolute', inset: 0, display: 'flex', width: `${count * 100}%`,
                    transform: `translateX(-${(current * 100) / count}%)`, transition: `transform ${slide}ms ease`}}>
                    {photos.map((p, i) => (
                        <Box key={`${p}-${i}`} sx={{width: `${100 / count}%`, height: '100%', flex: 'none'}}>
                            <img src={`${assetsUrl}/${p}`} alt={label ? `${label} ${i + 1}` : `Photo ${i + 1}`}
                                 onLoad={() => setLoaded((l) => (l[p] ? l : {...l, [p]: true}))}
                                 style={{width: '100%', height: '100%', objectFit: 'cover', display: 'block'}}/>
                        </Box>
                    ))}
                </Box>
            ) : (
                <Typography variant="caption" sx={{color: '#8A929C'}}>Aucune photo</Typography>
            )}
            {src && !loaded[src] && (
                <CircularProgress size={28} thickness={5} sx={{position: 'absolute', color: '#0F2D69', opacity: .7}}/>
            )}
            {label && <PhotoLabel label={label} color={color}/>}
            {count > 1 && (
                <>
                    <IconButton size="small" onClick={(e) => go(e, current - 1)} aria-label="Photo précédente"
                                sx={{position: 'absolute', left: 6, top: '50%', transform: 'translateY(-50%)', ...NAV_BTN_SX}}>
                        <ChevronLeftIcon fontSize="small"/>
                    </IconButton>
                    <IconButton size="small" onClick={(e) => go(e, current + 1)} aria-label="Photo suivante"
                                sx={{position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', ...NAV_BTN_SX}}>
                        <ChevronRightIcon fontSize="small"/>
                    </IconButton>
                    <Box sx={{position: 'absolute', bottom: 8, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 0.75}}>
                        {photos.map((p, i) => (
                            <Box key={`dot-${i}`} role="button" aria-label={`Photo ${i + 1}`} onClick={(e) => go(e, i)}
                                 sx={{width: 8, height: 8, borderRadius: '50%', cursor: 'pointer',
                                     bgcolor: i === current ? '#fff' : 'rgba(255,255,255,.5)', boxShadow: '0 0 2px rgba(16,46,105,.6)'}}/>
                        ))}
                    </Box>
                    <Box sx={{position: 'absolute', bottom: 8, right: 8, px: 0.75, py: 0.125, borderRadius: 999,
                        bgcolor: 'rgba(16,46,105,.72)', color: '#fff', fontSize: 11, fontWeight: 700}}>
                        {current + 1} / {count}
                    </Box>
                </>
            )}
        </Box>
    );
};

const NAV_BTN_SX = {
    bgcolor: 'rgba(255,255,255,.85)', color: '#0F2D69', boxShadow: '0 1px 3px rgba(16,46,105,.2)',
    '&:hover': {bgcolor: '#fff'},
};

/** Ligne à puce colorée (raisons / problèmes). */
export const BulletLine = ({color = '#B22222', children}) => (
    <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1.25, py: 0.5}}>
        <Box sx={{width: 8, height: 8, borderRadius: '50%', bgcolor: color, mt: '7px', flex: 'none'}}/>
        <Typography variant="body1" sx={{fontWeight: 600, color: '#2D333B'}}>{children}</Typography>
    </Box>
);

/**
 * Gabarit commun d'un bloc d'acte : header 12×20 teinté (numéro rond 26 px, picto, libellé
 * uppercase, valeur à droite) + corps blanc. `tokens` = NATURE_TOKENS[type].
 */
export const ActeBlock = ({numero, titre, picto, valeur, valeurColor, tokens, dashed = false, chip, children, headerAction}) => (
    <Box sx={{...CARD_SX, border: dashed ? '1px dashed #C9D4E8' : CARD_SX.border}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.5, bgcolor: tokens.bg, color: tokens.ink}}>
            <Box sx={{width: 26, height: 26, borderRadius: '50%', bgcolor: tokens.dot, color: '#fff', fontSize: 13, fontWeight: 800,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none'}}>
                {numero}
            </Box>
            <Picto name={picto ?? tokens.picto} sx={{fontSize: 20}}/>
            <Typography sx={{fontWeight: 800, fontSize: 13, letterSpacing: '.08em', textTransform: 'uppercase', flexGrow: 1, minWidth: 0}} noWrap>
                {titre}
            </Typography>
            {chip}
            {valeur && (
                <Typography sx={{fontWeight: 700, fontSize: 14, color: valeurColor ?? tokens.ink, whiteSpace: 'nowrap'}}>
                    {valeur}
                </Typography>
            )}
            {headerAction}
        </Box>
        {children && <Box sx={{p: 2.5}}>{children}</Box>}
    </Box>
);
